import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE, getToken } from "../../lib/api";
import { COLORS } from "../../lib/brand";
import { COUNTRIES, US_STATES, DOCUMENT_TYPES } from "../../lib/apostilleData";
import { ArrowLeft, ExternalLink, Mail, Phone, MapPin, FileText, Download, Copy, Plus, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "ready_for_processing", label: "Ready for Processing" },
  { value: "in_processing", label: "In Processing" },
  { value: "embassy_legalization", label: "Embassy Legalization" },
  { value: "complete", label: "Complete" },
  { value: "shipped", label: "Shipped / Ready for Pickup" },
];

const SERVICE_LABEL = {
  standard: "Standard State Apostille",
  expedited: "Expedited State Apostille",
  federal: "Federal Processing",
  embassy: "Embassy Legalization",
};

const DELIVERY_LABEL = {
  front_door: "Front Door / Courier",
  in_office_pickup: "In-Office Pickup",
  domestic_ship: "Domestic Shipping",
  intl_ship: "International Shipping",
};

export function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/apostille/orders").then((r) => setOrders(r.data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1A1A1A]">Apostille Orders</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Manage client orders and update status milestones.</p>
        </div>
        <Link
          to="/admin/orders/new"
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium text-white"
          style={{ background: COLORS.maroon }}
          data-testid="new-order-btn"
        >
          <Plus size={14} /> New Order (Call-In)
        </Link>
      </header>

      <div className="flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={orders.length} />
        {STATUS_OPTIONS.map((s) => {
          const c = orders.filter((o) => o.status === s.value).length;
          return <FilterChip key={s.value} active={filter === s.value} onClick={() => setFilter(s.value)} label={s.label} count={c} />;
        })}
      </div>

      <div className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[#5C5C5C]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">No orders.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF8F1] text-[#5C5C5C] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Client</th>
                <th className="text-left px-5 py-3">Service</th>
                <th className="text-left px-5 py-3">Destination</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Created</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D5]">
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-[#FBF8F1]/50">
                  <td className="px-5 py-4">
                    <div className="font-medium text-[#1A1A1A]">{o.first_name} {o.last_name}</div>
                    <div className="text-xs text-[#5C5C5C]">{o.email}</div>
                  </td>
                  <td className="px-5 py-4 text-[#1A1A1A]">{SERVICE_LABEL[o.service_level]}</td>
                  <td className="px-5 py-4 text-[#1A1A1A]">{o.destination_country}</td>
                  <td className="px-5 py-4">
                    <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: COLORS.cream, color: COLORS.maroon }}>
                      {STATUS_OPTIONS.find((s) => s.value === o.status)?.label || o.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-[#5C5C5C]">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/admin/orders/${o.id}`} className="text-[#800000] hover:text-[#5C0000] font-medium" data-testid={`order-row-open-${o.id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active ? "bg-[#800000] text-white border-[#800000]" : "bg-white text-[#5C5C5C] border-[#E8E2D5] hover:border-[#800000]"
      }`}
    >
      {label} <span className="opacity-70">· {count}</span>
    </button>
  );
}

export function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/apostille/orders/${id}`);
      setOrder(r.data);
      setDraft(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function patch(payload) {
    setSaving(true);
    try {
      const r = await api.patch(`/apostille/orders/${id}`, payload);
      setOrder({ ...order, ...r.data });
      setDraft({ ...order, ...r.data });
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function saveEdits() {
    // Diff draft vs order — only send changed fields
    const editable = [
      "first_name", "last_name", "company", "email", "phone",
      "return_street", "return_city", "return_state", "return_zip", "return_country",
      "document_count", "state_of_origin", "destination_country", "document_types",
      "service_level", "addon_translation", "translation_language",
      "addon_notarization", "addon_retrieval", "addon_intl_shipping",
      "delivery_method", "notes",
    ];
    const changes = {};
    for (const k of editable) {
      if (JSON.stringify(draft[k]) !== JSON.stringify(order[k])) {
        changes[k] = draft[k];
      }
    }
    if (Object.keys(changes).length === 0) {
      setEditing(false);
      return;
    }
    await patch(changes);
    setEditing(false);
  }

  function cancelEdits() {
    setDraft(order);
    setEditing(false);
  }

  function copyTrackingLink() {
    const url = `${window.location.origin}/track/${order.tracking_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Tracking link copied");
  }

  async function downloadFile(fileId, filename) {
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download file");
    }
  }

  if (loading) return <div className="text-sm text-[#5C5C5C]">Loading...</div>;
  if (!order) return <div className="text-sm text-[#5C5C5C]">Order not found.</div>;

  return (
    <div className="space-y-6" data-testid="admin-order-detail">
      <button onClick={() => navigate(-1)} className="text-sm text-[#5C5C5C] hover:text-[#800000] flex items-center gap-1">
        <ArrowLeft size={14} /> Back
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A1A]">{order.first_name} {order.last_name}</h1>
          <div className="text-sm text-[#5C5C5C] mt-1">Order #{order.id.slice(0, 8)} · created {new Date(order.created_at).toLocaleString()}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyTrackingLink} className="text-sm border border-[#E8E2D5] px-3 py-2 rounded-md flex items-center gap-2 hover:border-[#800000]" data-testid="copy-tracking-link">
            <Copy size={14} /> Copy tracking link
          </button>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-2" style={{ background: COLORS.maroon }} data-testid="edit-order-btn">
              <Pencil size={14} /> Edit Order
            </button>
          ) : (
            <>
              <button onClick={cancelEdits} className="text-sm border border-[#E8E2D5] px-3 py-2 rounded-md flex items-center gap-2 hover:border-[#800000]" data-testid="cancel-edits-btn">
                <X size={14} /> Cancel
              </button>
              <button onClick={saveEdits} disabled={saving} className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-2" style={{ background: COLORS.maroon }} data-testid="save-edits-btn">
                <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Client">
            {editing ? (
              <ClientEditFields draft={draft} setDraft={setDraft} />
            ) : (
              <>
                <Row icon={<Mail size={14} />} value={order.email} />
                <Row icon={<Phone size={14} />} value={order.phone} />
                {order.company && <Row label="Company" value={order.company} />}
                <Row icon={<MapPin size={14} />} value={`${order.return_street}, ${order.return_city}, ${order.return_state} ${order.return_zip}, ${order.return_country}`} />
              </>
            )}
          </Card>

          <Card title="Document & Service">
            {editing ? (
              <DocumentEditFields draft={draft} setDraft={setDraft} />
            ) : (
              <>
                <Row label="Service Level" value={SERVICE_LABEL[order.service_level]} />
                <Row label="State of Origin" value={order.state_of_origin} />
                <Row label="Destination Country" value={order.destination_country} />
                <Row label="Document Count" value={order.document_count} />
                <Row label="Document Types" value={(order.document_types || []).join(", ") || "—"} />
                <Row label="Delivery" value={DELIVERY_LABEL[order.delivery_method]} />
                <Row label="Add-ons" value={[
                  order.addon_translation && `Translation${order.translation_language ? ` (${order.translation_language})` : ""}`,
                  order.addon_notarization && "Notarization",
                  order.addon_retrieval && "Document Retrieval",
                  order.addon_intl_shipping && "International Shipping",
                ].filter(Boolean).join(", ") || "—"} />
                {order.notes && <Row label="Client Notes" value={order.notes} />}
              </>
            )}
          </Card>

          <Card title={`Uploaded Documents (${(order.files || []).length})`}>
            {(order.files || []).length === 0 ? (
              <div className="text-sm text-[#5C5C5C]">No files uploaded.</div>
            ) : (
              <ul className="space-y-2">
                {order.files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between border border-[#E8E2D5] rounded-md px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-[#800000]" />
                      <span className="text-sm truncate">{f.original_filename}</span>
                      <span className="text-xs text-[#5C5C5C]">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <button onClick={() => downloadFile(f.id, f.original_filename)} className="text-sm text-[#800000] hover:text-[#5C0000] flex items-center gap-1" data-testid={`download-file-${f.id}`}>
                      <Download size={14} /> Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Right: actions */}
        <div className="space-y-6">
          <Card title="Status & Milestones">
            <label className="block text-xs uppercase tracking-wider text-[#5C5C5C] mb-1">Current Status</label>
            <select
              value={order.status}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full px-3 py-2 border border-[#E8E2D5] rounded-md mb-4"
              disabled={saving}
              data-testid="order-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            <label className="block text-xs uppercase tracking-wider text-[#5C5C5C] mb-1">Estimated Ready Date</label>
            <input
              type="date"
              defaultValue={order.estimated_ready_date || ""}
              onBlur={(e) => e.target.value !== (order.estimated_ready_date || "") && patch({ estimated_ready_date: e.target.value || null })}
              className="w-full px-3 py-2 border border-[#E8E2D5] rounded-md mb-4"
              data-testid="order-eta-input"
            />

            <label className="block text-xs uppercase tracking-wider text-[#5C5C5C] mb-1">Shipping Tracking Link</label>
            <input
              type="url"
              placeholder="https://fedex.com/track/..."
              defaultValue={order.tracking_link || ""}
              onBlur={(e) => e.target.value !== (order.tracking_link || "") && patch({ tracking_link: e.target.value || null })}
              className="w-full px-3 py-2 border border-[#E8E2D5] rounded-md mb-4"
              data-testid="order-tracking-link-input"
            />

            <label className="block text-xs uppercase tracking-wider text-[#5C5C5C] mb-1">Admin Notes</label>
            <textarea
              defaultValue={order.admin_notes || ""}
              onBlur={(e) => e.target.value !== (order.admin_notes || "") && patch({ admin_notes: e.target.value })}
              className="w-full px-3 py-2 border border-[#E8E2D5] rounded-md min-h-[100px]"
              data-testid="order-admin-notes"
            />
          </Card>

          <Card title="Client tracking page">
            <a
              href={`/track/${order.tracking_token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#800000] hover:text-[#5C0000] flex items-center gap-1"
              data-testid="open-client-tracking"
            >
              <ExternalLink size={14} /> Open client view
            </a>
          </Card>

          <Card title="Notify Customer">
            <button
              disabled
              title="Email/SMS provider not configured yet"
              className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-[#FBF8F1] text-[#5C5C5C] border border-dashed border-[#E8E2D5] cursor-not-allowed"
              data-testid="notify-customer-btn"
            >
              Notify Customer for Pickup (coming soon)
            </button>
            <p className="text-xs text-[#5C5C5C] mt-2">Wire up an email/SMS provider to enable.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm">
      <div className="px-5 py-4 border-b border-[#E8E2D5]">
        <h3 className="font-medium text-[#1A1A1A]">{title}</h3>
      </div>
      <div className="px-5 py-4 space-y-2">{children}</div>
    </section>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="text-[#5C5C5C] mt-0.5">{icon}</span>}
      {label && <span className="text-[#5C5C5C] w-32 shrink-0">{label}</span>}
      <span className="text-[#1A1A1A] break-words">{value}</span>
    </div>
  );
}

const editInputCls = "w-full px-2.5 py-1.5 border border-[#E8E2D5] rounded text-sm focus:outline-none focus:border-[#800000] focus:ring-1 focus:ring-[#800000]";

function EditField({ label, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C] block mb-1">{label}</span>
      {children}
    </label>
  );
}

function ClientEditFields({ draft, setDraft }) {
  const u = (patch) => setDraft({ ...draft, ...patch });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="client-edit-fields">
      <EditField label="First Name"><input className={editInputCls} value={draft.first_name || ""} onChange={(e) => u({ first_name: e.target.value })} /></EditField>
      <EditField label="Last Name"><input className={editInputCls} value={draft.last_name || ""} onChange={(e) => u({ last_name: e.target.value })} /></EditField>
      <EditField label="Company"><input className={editInputCls} value={draft.company || ""} onChange={(e) => u({ company: e.target.value })} /></EditField>
      <EditField label="Email"><input type="email" className={editInputCls} value={draft.email || ""} onChange={(e) => u({ email: e.target.value })} /></EditField>
      <EditField label="Phone"><input className={editInputCls} value={draft.phone || ""} onChange={(e) => u({ phone: e.target.value })} /></EditField>
      <EditField label="Street" span={2}><input className={editInputCls} value={draft.return_street || ""} onChange={(e) => u({ return_street: e.target.value })} /></EditField>
      <EditField label="City"><input className={editInputCls} value={draft.return_city || ""} onChange={(e) => u({ return_city: e.target.value })} /></EditField>
      <EditField label="State"><input className={editInputCls} value={draft.return_state || ""} onChange={(e) => u({ return_state: e.target.value })} /></EditField>
      <EditField label="ZIP"><input className={editInputCls} value={draft.return_zip || ""} onChange={(e) => u({ return_zip: e.target.value })} /></EditField>
      <EditField label="Country">
        <select className={editInputCls} value={draft.return_country || "United States"} onChange={(e) => u({ return_country: e.target.value })}>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </EditField>
    </div>
  );
}

function DocumentEditFields({ draft, setDraft }) {
  const u = (patch) => setDraft({ ...draft, ...patch });
  const toggleDoc = (item) => {
    const list = draft.document_types || [];
    u({ document_types: list.includes(item) ? list.filter((d) => d !== item) : [...list, item] });
  };
  return (
    <div className="space-y-4" data-testid="document-edit-fields">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <EditField label="Service Level">
          <select className={editInputCls} value={draft.service_level} onChange={(e) => u({ service_level: e.target.value })}>
            <option value="standard">Standard State Apostille</option>
            <option value="expedited">Expedited State Apostille</option>
            <option value="federal">Federal Processing</option>
            <option value="embassy">Embassy Legalization</option>
          </select>
        </EditField>
        <EditField label="State of Origin">
          <select className={editInputCls} value={draft.state_of_origin} onChange={(e) => u({ state_of_origin: e.target.value })}>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </EditField>
        <EditField label="Destination Country">
          <select className={editInputCls} value={draft.destination_country} onChange={(e) => u({ destination_country: e.target.value })}>
            <option value="">Select...</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </EditField>
        <EditField label="Document Count">
          <input type="number" min={1} className={editInputCls} value={draft.document_count} onChange={(e) => u({ document_count: Math.max(1, parseInt(e.target.value || "1")) })} />
        </EditField>
        <EditField label="Delivery Method" span={2}>
          <select className={editInputCls} value={draft.delivery_method} onChange={(e) => u({ delivery_method: e.target.value })}>
            <option value="front_door">Front Door / Courier Delivery</option>
            <option value="in_office_pickup">In-Office Pickup</option>
            <option value="domestic_ship">Domestic Shipping</option>
            <option value="intl_ship">International Shipping</option>
          </select>
        </EditField>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mb-2">Document Types</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {DOCUMENT_TYPES.map((g) => (
            <div key={g.group} className="border border-[#E8E2D5] rounded-md p-2.5">
              <div className="text-[10px] font-medium text-[#800000] uppercase tracking-wider mb-1.5">{g.group}</div>
              <div className="space-y-1">
                {g.items.map((item) => (
                  <label key={item} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={(draft.document_types || []).includes(item)} onChange={() => toggleDoc(item)} className="accent-[#800000]" />
                    <span className="text-[#1A1A1A]">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mb-2">Add-Ons</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.addon_translation} onChange={(e) => u({ addon_translation: e.target.checked })} className="accent-[#800000]" /> Translation</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.addon_notarization} onChange={(e) => u({ addon_notarization: e.target.checked })} className="accent-[#800000]" /> Notarization</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.addon_retrieval} onChange={(e) => u({ addon_retrieval: e.target.checked })} className="accent-[#800000]" /> Document Retrieval</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.addon_intl_shipping} onChange={(e) => u({ addon_intl_shipping: e.target.checked })} className="accent-[#800000]" /> International Shipping</label>
        </div>
        {draft.addon_translation && (
          <input
            placeholder="Translation target language"
            value={draft.translation_language || ""}
            onChange={(e) => u({ translation_language: e.target.value })}
            className={`${editInputCls} mt-2`}
          />
        )}
      </div>

      <EditField label="Client Notes">
        <textarea className={`${editInputCls} min-h-[60px]`} value={draft.notes || ""} onChange={(e) => u({ notes: e.target.value })} />
      </EditField>
    </div>
  );
}
